import type { JSX } from 'react';
import {
  measureSms,
  SMS_MAX_SEGMENTS,
  type SmsEncoding,
  smsTooLongMessage,
} from '@majuscule/domain';
import { useState } from 'react';
import {
  ENCODING_LABELS,
  formatMicroEur,
  RULE_SOURCE_LABELS,
  type RuleSource,
} from '../lib/use-cases';
import { Field, FieldError } from '@/components/ui/field';
import { KvList, KvRow } from '@/components/ui/kv';
import { Textarea } from '@/components/ui/textarea';

/**
 * Capacity of a single SMS, then of each part of a longer one, as `measureSms`
 * packs them. Display only: the segments themselves come from the domain.
 */
const CAPACITY: Record<SmsEncoding, { single: number; perPart: number }> = {
  gsm7: { single: 160, perPart: 153 },
  ucs2: { single: 70, perPart: 67 },
};

/** The text as `measureSms` normalizes it, before any look-alike conversion. */
function normalized(text: string): string {
  return text.normalize('NFC').replace(/\r\n/g, '\n');
}

/**
 * Live SMS simulator (lot 17): segments, encoding and estimated price of a test
 * message. Everything comes from the domain `measureSms`, the function that
 * also counts the billed SMS and drives what Vonage sends, so what the operator
 * sees is what will be billed. The test message stays in the browser.
 */
export function SmsSimulator({
  pricePerSmsMicroEur,
  priceSource,
}: {
  /** Selling price of one SMS, or null when unknown (no estimate shown). */
  pricePerSmsMicroEur: number | null;
  /** Rule the price comes from, named next to the estimate; omitted for a price being typed. */
  priceSource?: RuleSource | undefined;
}): JSX.Element {
  const [text, setText] = useState('');
  const measure = measureSms(text);
  const { single, perPart } = CAPACITY[measure.encoding];
  const capacity = measure.segments === 1 ? single : measure.segments * perPart;
  // Look-alikes (’ « » – …) were replaced only if that kept the message in GSM-7.
  const converted = measure.encoding === 'gsm7' && measure.text !== normalized(text);
  const tooLong = measure.segments > SMS_MAX_SEGMENTS;

  return (
    <div className="grid gap-3">
      <Field
        label="Message d’essai"
        hint={
          text.length === 0
            ? 'Saisir un message pour voir le nombre de SMS, l’encodage et le prix.'
            : undefined
        }
      >
        <Textarea
          value={text}
          onChange={(event) => {
            setText(event.target.value);
          }}
          maxLength={1600}
          rows={3}
          placeholder="Votre commande CMD-2026-0142 est prête : retrait possible dès demain en magasin."
        />
      </Field>
      {text.length === 0 ? null : (
        <div className="grid gap-2">
          <KvList>
            <KvRow label="Longueur" mono>
              {measure.length}/{capacity} caractères
            </KvRow>
            <KvRow label="Encodage">
              {ENCODING_LABELS[measure.encoding]}
              {measure.encoding === 'ucs2'
                ? `, à cause de : ${measure.nonGsmCharacters.join(', ')}`
                : ''}
            </KvRow>
            <KvRow label="Segments" mono>
              {measure.segments} SMS
            </KvRow>
            {!tooLong && pricePerSmsMicroEur !== null ? (
              <KvRow
                label={
                  priceSource === undefined
                    ? 'Prix estimé'
                    : `Prix estimé (${RULE_SOURCE_LABELS[priceSource]})`
                }
                mono
              >
                {measure.segments} × {formatMicroEur(pricePerSmsMicroEur)} ={' '}
                <span className="font-medium">
                  {formatMicroEur(measure.segments * pricePerSmsMicroEur)} HT
                </span>
              </KvRow>
            ) : null}
          </KvList>
          {converted ? (
            <p className="m-0 text-caption text-muted-foreground">
              Caractères typographiques (’ « » – … espaces insécables) convertis en équivalents
              GSM-7 : le message reste en GSM-7.
            </p>
          ) : null}
          {tooLong ? <FieldError>{smsTooLongMessage(measure.segments)}</FieldError> : null}
        </div>
      )}
    </div>
  );
}
